import * as React from 'react';
import * as ReactDOM from 'react-dom';

import BuildClient = require("Build.Common/Scripts/Api2.2/ClientServices");

import Dashboard_Shared_Contracts = require("Dashboards/Scripts/Contracts");
import { SettingsField } from "Dashboards/Scripts/SettingsField";

import Build_Contracts = require("TFS/Build/Contracts");
import WidgetContracts = require("TFS/Dashboards/WidgetContracts");
import WidgetHelpers = require("TFS/Dashboards/WidgetHelpers");

import Artifacts_Services = require("VSS/Artifacts/Services");
import Controls = require("VSS/Controls");
import SDK = require("VSS/SDK/Shim");
import Service = require("VSS/Service");

import Base = require("Widgets/Scripts/VSS.Control.BaseWidgetConfiguration");
import BuildDefinitionPicker = require("Dashboards/Controls/Pickers/BuildDefinitionPicker");
import BuildWidget = require("Widgets/Scripts/BuildChart");
import Resources_Widgets = require("Widgets/Scripts/Resources/TFS.Resources.Widgets");
import Widget_Telemetry = require("Widgets/Scripts/VSS.Widget.Telemetry");
import Widget_Utils = require("Widgets/Scripts/TFS.Widget.Utilities");

import { Checkbox } from "VSSUI/Checkbox";

import { TfsContext } from "Presentation/Scripts/TFS/TFS.Host.TfsContext";

import { BranchSelector } from "CIWorkflow/Scripts/Scenarios/Definition/Components/Filters";

import "VSS/LoaderPlugins/Css!VersionControlControls";
import "VSS/LoaderPlugins/Css!PivotView";

const c_repoTypesToPrefixRefsHeads: string[] = ["GitHub", "GitHubEnterprise", "Bitbucket", "TfsGit"];
const c_refsHeads = "refs/heads/";

/**
* DOM classes associated with the Build Chart Configuration.
*/
export class BuildHistogramConfigurationDomClasses {
    public static widgetContainer: string = "buildchartconfiguration-container";
}

interface IBranchPickerProps {
    onBranchChange: (branch: string) => void;
    buildRepository?: Build_Contracts.BuildRepository;
    selectedBranch?: string;
}

interface IBranchPickerState {
    includeAllBranches: boolean;
    selectedBranch: string;
}

class BranchPicker extends React.Component<IBranchPickerProps, IBranchPickerState> {
    private defaultBranch: string = this.props.buildRepository ? this.props.buildRepository.defaultBranch : undefined;

    constructor(props: IBranchPickerProps) {
        super(props);
        this.state = {
            includeAllBranches: !props.selectedBranch,
            selectedBranch: props.selectedBranch ? props.selectedBranch : null
        }
    }

    public render() {
        return <div>
            <div className="branch-filter-checkbox-container">
                <Checkbox
                    label={Resources_Widgets.BuildHistogramConfiguration_AllBranchesLabel}
                    onChange={this.handleCheckboxChanged}
                    checked={this.state.includeAllBranches}
                    disabled={!this.props.buildRepository}
                />
            </div>
            {this.props.buildRepository && !this.state.includeAllBranches && <div className="build-histogram-configuration-branch-picker">
                <BranchSelector
                    filter={this.state.selectedBranch}
                    onFilterChange={this.handleBranchChanged}
                    repository={this.props.buildRepository}
                    repositoryType={this.props.buildRepository.type}
                />
            </div>}
        </div>
    }

    private handleCheckboxChanged = (e: React.FormEvent<HTMLElement>, isChecked: boolean) => {
        const newState = { includeAllBranches: isChecked } as any;
        if (isChecked) {
            this.props.onBranchChange(null);
        } else if (this.state.selectedBranch) {
            this.handleBranchChanged(this.state.selectedBranch);
        } else {
            this.handleBranchChanged(this.defaultBranch);
        }
        this.setState(newState);
    }

    private handleBranchChanged = (newBranch: string) => {
        // We need to append refs/heads/ to GitHub branch names in order to be able get the builds for the branch
        let fullBranchName: string = newBranch;
        if (newBranch && c_repoTypesToPrefixRefsHeads.filter(e => e.toUpperCase() === this.props.buildRepository.type.toUpperCase()).length > 0) {
            if (!newBranch.startsWith(c_refsHeads)) {
                fullBranchName = c_refsHeads + newBranch;
            }

        }
        this.props.onBranchChange(fullBranchName);
        this.setState({ selectedBranch: newBranch });
    }
}

export interface IBuildHistogramConfiguration {
    buildDefinition: BuildWidget.BuildDefinitionReference,
    fullBranchName: string
}

export class BuildHistogramConfiguration
    extends Base.BaseWidgetConfiguration<Dashboard_Shared_Contracts.WidgetConfigurationOptions>
    implements WidgetContracts.IWidgetConfiguration {
    public static get DefaultConfiguration(): IBuildHistogramConfiguration {
        return {
            buildDefinition: null,
            fullBranchName: null
        }
    };
    private static DefaultWidgetName = Resources_Widgets.BuildHistogram_DefaultWidgetName;
    private $container: JQuery = $("<div/>");
    private definitionSettingsField: SettingsField<any>;
    private currentConfiguration: IBuildHistogramConfiguration = BuildHistogramConfiguration.DefaultConfiguration;
    private newWidgetConfigurationContext: WidgetContracts.IWidgetConfigurationContext;
    private providers: BuildDefinitionPicker.IBuildDefinitionDataProvider[] = [];
    private buildClient: BuildClient.BuildClientService = Service.getCollectionService(BuildClient.BuildClientService);

    public initializeOptions(options?: any) {
        super.initializeOptions(Object.assign({
            coreCssClass: BuildHistogramConfigurationDomClasses.widgetContainer
        }, options));
    }

    public initialize() {
        super.initialize();
        this.providers = (this._options as any).providers;
    }

    /**
     * @implements {WidgetContracts.IWidgetConfiguration}
     */
    public load(widgetSettings: WidgetContracts.WidgetSettings,
        widgetConfigurationContext: WidgetContracts.IWidgetConfigurationContext): IPromise<WidgetContracts.WidgetStatus> {

        this.newWidgetConfigurationContext = widgetConfigurationContext;

        // parse the current settings of the widget from configuration context. 
        this.currentConfiguration = BuildHistogramConfiguration.stringToConfiguration(widgetSettings.customSettings.data);

        this.render();

        return WidgetHelpers.WidgetStatusHelper.Success();
    }

    public static stringToConfiguration(configurationString: string): IBuildHistogramConfiguration {
        let configuration = BuildHistogramConfiguration.DefaultConfiguration;
        try {
            configuration = JSON.parse(configurationString) as IBuildHistogramConfiguration;

            // parse the definitionId from the its url if it doesnt already exists. This can happen if the widget was pinned and then configured. 
            if (configuration.buildDefinition) {
                configuration.buildDefinition.id = parseInt(Artifacts_Services.LinkingUtilities.decodeUri(configuration.buildDefinition.uri).id);

                // get the name from settings if available, or pull it from the Widget Name field. This will be the default name in case of catalog added 
                // widgets and last definition name in case of pinned widgets. 
                configuration.buildDefinition.name = configuration.buildDefinition.name || BuildHistogramConfiguration.DefaultWidgetName;
            }

        }
        catch (e) {
            return BuildHistogramConfiguration.DefaultConfiguration;
        }
        return configuration;
    }

    public render(): void {
        var buildDefinitionPickerOptions: BuildDefinitionPicker.BuildDefinitionPickerOptionsInternal = {
            onIndexChanged: this.handleBuildDefinitionChanged,
            dataProviders: this.providers
        };

        if (this.currentConfiguration && this.currentConfiguration.buildDefinition) {
            buildDefinitionPickerOptions.initialValue = this.convertToPickerFormat(this.currentConfiguration.buildDefinition);
        }

        BuildDefinitionPicker.create(this.getContainer(), buildDefinitionPickerOptions);

        this.definitionSettingsField = SettingsField.createSettingsFieldForJQueryElement({
            labelText: Resources_Widgets.BuildChartConfiguration_Label,
            initialErrorMessage: Resources_Widgets.BuildChartConfiguration_ErrorNoDefinitionSelected,
        }, this.getContainer(), null);

        this.getElement().append(this.definitionSettingsField.getElement());

        if (this.currentConfiguration.buildDefinition) {
            this.renderBranchPicker(this.currentConfiguration.buildDefinition.id)
        }
    }

    // Render the branch picker component and remove the old one if exists
    private renderBranchPicker(definitionId: number) {        
        // Fetch the full definition in order to get the repository
        this.buildClient.getDefinition(definitionId).then(buildDefinition => {            
            const branchSettingsFieldElement = SettingsField.createSettingsField({
                labelText: Resources_Widgets.BuildHistogramConfiguration_BranchSectionHeader,
                control: null
            }).getElement();

            const branchPickerClassName = "build-histogram-branch-picker";
            branchSettingsFieldElement.addClass(branchPickerClassName)
            // The checkbox component has its own styling that does not work with this class.
            branchSettingsFieldElement.removeClass("bowtie");            

            const branchPickerContainer = document.createElement("div");

            const branchName = this.currentConfiguration.fullBranchName;
            ReactDOM.render(<BranchPicker
                buildRepository={buildDefinition ? (buildDefinition as Build_Contracts.BuildDefinition).repository : undefined}
                selectedBranch={branchName ? branchName.replace(c_refsHeads, "") : undefined}
                onBranchChange={this.handleBranchChanged}
            />, branchPickerContainer);

            branchSettingsFieldElement.append(branchPickerContainer);
            // Remove the previous branch picker and replace with new one
            this.getElement().find("." + branchPickerClassName).remove();
            this.getElement().append(branchSettingsFieldElement);
        });
    }

    public convertToPickerFormat(original: BuildWidget.BuildDefinitionReference): Build_Contracts.DefinitionReference {
        return {
            name: original.name,
            id: original.id,
            type: original.type
        } as Build_Contracts.DefinitionReference;
    }

    /**
    * Representation of the container that actually holds the configuration UI.
    * @returns a JQuery element
    */
    public getContainer(): JQuery {
        return this.$container;
    }

    /**
     * @implements {WidgetContracts.IWidgetConfiguration}
     */
    public onSave(): IPromise<WidgetContracts.SaveStatus> {
        this.repaintErrors();

        if (this.isValid()) {
            return WidgetHelpers.WidgetConfigurationSave.Valid(this._getCustomSettings());
        } else {
            return WidgetHelpers.WidgetConfigurationSave.Invalid();
        }
    }

    public _getCustomSettings(): WidgetContracts.CustomSettings {
        return { data: JSON.stringify(this.getCurrentConfiguration()) };
    }

    /** 
    * Reports to host on current state of widget 
    */
    public notifyConfigurationChange() {
        this.repaintErrors();
        if (this.isValid()) {
            this.newWidgetConfigurationContext.notify(WidgetHelpers.WidgetEvent.ConfigurationChange, WidgetHelpers.WidgetEvent.Args(this._getCustomSettings()));
        }
    }

    /**
    * Repaint errors if the validation of the configuration entails as such.
    */
    public repaintErrors(): void {
        this.definitionSettingsField.toggleError(this.hasError());
    }

    private titleHasBeenCustomized() {
        const currentTitle = this._options.getCurrentWidgetName();
        if (this.currentConfiguration.buildDefinition) {
            return currentTitle !== this.currentConfiguration.buildDefinition.name;
        }
        return currentTitle !== BuildHistogramConfiguration.DefaultWidgetName;
    }

    /**
    * Operations to perform on callback when the selection of definitions changes. 
    */
    private handleBuildDefinitionChanged = (definition: Build_Contracts.DefinitionReference): void => {
        // update the configuration title input value to the new build definition name if the title has not been customized
        if (!this.titleHasBeenCustomized()) {
            this._options.setCurrentWidgetName(definition.name);
        }

        // Only re-render the branch picker if the selection has changed
        const currentDefinitionId = this.currentConfiguration.buildDefinition ? this.currentConfiguration.buildDefinition.id : undefined;
        if (definition && definition.id != currentDefinitionId) {
            this.renderBranchPicker(definition.id);
            this.currentConfiguration.fullBranchName = null;
        }
        
        // update current configuration based on selected item.
        this.currentConfiguration.buildDefinition = this.convertFromPickerFormat(definition);        

        // repaint errors in case of validation issues. 
        this.repaintErrors();

        // notify the configuaration host of a change. 
        this.notifyConfigurationChange();
    }

    private handleBranchChanged = (branch: string) => {
        this.currentConfiguration.fullBranchName = branch;

        this.repaintErrors()
        this.notifyConfigurationChange()
    }

    public convertFromPickerFormat(original: Build_Contracts.DefinitionReference): BuildWidget.BuildDefinitionReference {
        return {
            name: BuildDefinitionPicker.getDefinitionNodeText(original),
            id: original.id,
            type: original.type,
            uri: original.uri,
            projectId: original.project.id
        } as BuildWidget.BuildDefinitionReference;
    }

    /**
    * verify if the current configuration is valid or not. 
    * @returns boolean
    */
    public isValid(): boolean {
        return !Widget_Utils.isUndefinedOrNull(this.currentConfiguration) && !Widget_Utils.isUndefinedOrNull(this.currentConfiguration.buildDefinition);
    }

    /**
    * verify if experience would have an error
    * @returns boolean
    */
    private hasError(): boolean {
        return !this.isValid();
    }

    /**
    * Get the latest configuration
    * @returns reference to the definition. 
    */
    public getCurrentConfiguration(): IBuildHistogramConfiguration {
        return this.currentConfiguration;
    }

    /**
    * @implements {WidgetContracts.IWidgetConfiguration}
    */
    public onSaveComplete(): void {

        // Create the property payload
        var isCustomName: boolean = this.currentConfiguration.buildDefinition && this.configureName.getCurrentWidgetName() != this.currentConfiguration.buildDefinition.name;
        var properties: IDictionaryStringTo<any> = {
            "IsCustomName": isCustomName
        }

        if (isCustomName) {
            properties["NameLength"] = this.currentConfiguration.buildDefinition.name.length;
        }

        // Publish
        Widget_Telemetry.WidgetTelemetry.onConfigurationSave(this.getWidgetTypeId(), properties);
    }
}

SDK.registerContent("dashboards.buildHistogramConfiguration-init", (context) => {
    return Controls.create(
        BuildHistogramConfiguration,
        context.$container,
        context.options);
});
